// Native status editor; mutations retain the existing browser callbacks.
import type { EditCtx, StatusEntry, StatusTemplateArr } from './types';
import { pick, str, num, flagOn } from './read';
import { iconHtml, iconSpan, section, clickableRef, numberInput } from './dom';
import { markStatusEffectDeleted, isPaused, isCraftingStatusEntry, statusRow } from './status-row';
import { INVENTORY_HIDDEN_ID } from '../janus-engine/inventory/hidden-plan';
import { statusCampaignId, statusEncounterId } from './resolve-refs';

/** A show/hide toggle-pair button (privacy public⇄private, hide hide⇄reveal). Mutates on click, then flips. */
function togglePair(opts: {
    idA: string; idB: string; classA: string; classB: string; htmlA: string; htmlB: string;
    showA: boolean; onA: () => void; onB: () => void; titleA?: string; titleB?: string;
}): HTMLElement {
    const frag = document.createElement('span');
    const mk = (id: string, cls: string, html: string, show: boolean, on: () => void, title?: string): HTMLButtonElement => {
        const b = document.createElement('button');
        b.id = id; b.type = 'button'; b.className = cls; b.style.display = show ? 'block' : 'none';
        if (title) { b.title = title; b.setAttribute('aria-label', title); }
        b.innerHTML = html;
        b.addEventListener('click', () => {
            on();
            b.style.display = 'none';
            const twin = frag.querySelector<HTMLElement>(`[id="${id === opts.idA ? opts.idB : opts.idA}"]`);
            if (twin) twin.style.display = 'block';
        });
        return b;
    };
    frag.appendChild(mk(opts.idA, opts.classA, opts.htmlA, opts.showA, opts.onA, opts.titleA));
    frag.appendChild(mk(opts.idB, opts.classB, opts.htmlB, !opts.showA, opts.onB, opts.titleB));
    return frag;
}

function timeBlock(entry: StatusEntry, prefix: 'starts' | 'ends', glyph: string, label: string): HTMLElement {
    const wrap = section();
    const head = document.createElement('div');
    head.className = 'border-bottom mb-2 pb-2';
    head.appendChild(iconSpan(glyph));
    head.appendChild(document.createTextNode(` ${label}`));
    wrap.appendChild(head);

    const fields = (keys: Array<[string, string]>): HTMLElement => {
        const row = document.createElement('div');
        row.className = 'form-row mb-3';
        keys.forEach(([k, lbl]) => row.appendChild(numberInput(
            `status_${prefix}_campaign_${k}`, lbl, str(pick(entry, `status_${prefix}_campaign_${k}`)),
        )));
        return row;
    };
    wrap.appendChild(fields([['year', 'Year'], ['month', 'Month'], ['day', 'Day']]));
    wrap.appendChild(fields([['hour', 'Hour'], ['minute', 'Minute'], ['second', 'Second']]));
    return wrap;
}

export function renderStatusModifyForm(entry: StatusEntry, ctx: EditCtx, container: HTMLElement, opts: { compact?: boolean } = {}): void {
    const def: StatusTemplateArr = typeof entry.status_arr === 'object' ? entry.status_arr : {};
    const tmpl = (def.items ?? {}) as Record<string, unknown>;
    const effectName = str(tmpl.item_name) || 'Status Effect';
    const effectType = str(tmpl.item_type);
    const templateId = str(tmpl.item_id);
    const statusId = ctx.statusId;
    const isPrivate = num(pick(entry, 'status_private')) === 1;
    const root = `${BASE_URL}`;
    // Compact = embedded BELOW the read-only info panel (status-effect card "merge"): the panel already shows
    // the name + caster/target/origin, so skip those here and render only the EDIT logic (buttons + times + save).
    const compact = opts.compact === true;

    const wrap = document.createElement('div');
    wrap.className = 'row gx-0';

    // Title — skipped in compact (the info panel above already shows the effect name/icons).
    if (!compact) {
        const titleCol = document.createElement('div');
        titleCol.className = 'col-12 p-2';
        const h4 = document.createElement('h4'); h4.className = 'mb-2 pb-0'; h4.textContent = 'Modify Status Effect';
        titleCol.appendChild(h4);
        const nameRow = document.createElement('div'); nameRow.className = 'd-flex';
        const nameLeft = document.createElement('div'); nameLeft.className = 'flex-grow-1';
        const nameH = document.createElement('h5'); nameH.textContent = effectName; nameLeft.appendChild(nameH);
        const nameRight = document.createElement('div'); const iconsH = document.createElement('h5');
        const link = (id: string, glyph: string): HTMLElement => {
            const a = document.createElement('a'); a.href = `${root}item.php?id=${encodeURIComponent(id)}`;
            a.appendChild(iconSpan(glyph)); return a;
        };
        if (templateId) { iconsH.appendChild(link(templateId, templateId)); iconsH.appendChild(document.createTextNode(' ')); }
        if (statusId) iconsH.appendChild(link(statusId, effectType || 'status_effect'));
        nameRight.appendChild(iconsH);
        nameRow.appendChild(nameLeft); nameRow.appendChild(nameRight);
        titleCol.appendChild(nameRow);
        wrap.appendChild(titleCol);
    }

    // Action button row (all mutations reuse existing template.js fns / endpoints — ping only on click)
    const btnRow = document.createElement('div');
    btnRow.className = 'd-flex col-12 border rounded p-3 mb-3 flex-wrap align-items-center';

    // Reuse the shared row's stack controls and count; there is only one stack model/renderer.
    const stackRow = statusRow(entry, ctx, { canEdit: true, canCampaign: false });
    const down = stackRow.querySelector('[aria-label="Decrease stack"]');
    const up = stackRow.querySelector('[aria-label="Increase stack"]');
    if (down && up) {
        const stackWrap = document.createElement('div');
        stackWrap.className = 'p-2 d-flex align-items-center gap-1';
        const count = down.nextElementSibling;
        stackWrap.appendChild(down);
        if (count) stackWrap.appendChild(count);
        stackWrap.appendChild(up);
        btnRow.appendChild(stackWrap);
    }

    const privWrap = document.createElement('div'); privWrap.className = 'p-2';
    privWrap.appendChild(togglePair({
        idA: `status_effect_public_${statusId}`, idB: `status_effect_private_${statusId}`,
        classA: 'btn btn-primary btn-sm', classB: 'btn btn-danger btn-sm',
        htmlA: iconHtml('public') || 'Public', htmlB: iconHtml('private') || 'Private',
        showA: !isPrivate,
        onA: () => typeof queryData === 'function' && queryData(`${root}options/db_queries/update/update_privacy_inc.php`, 'div_sidelog_warning', { item_type: 'status_effect', status_effect_id: statusId, status_effect_private: '1' }),
        onB: () => typeof queryData === 'function' && queryData(`${root}options/db_queries/update/update_privacy_inc.php`, 'div_sidelog_warning', { item_type: 'status_effect', status_effect_id: statusId, status_effect_private: '0' }),
    }));
    btnRow.appendChild(privWrap);

    const hideWrap = document.createElement('div'); hideWrap.className = 'p-2';
    hideWrap.appendChild(togglePair({
        idA: `btn_hide_status_effect_${statusId}`, idB: `btn_reveal_status_effect_${statusId}`,
        classA: 'btn btn-dark border btn-sm', classB: 'btn btn-light border btn-sm',
        htmlA: iconHtml('hide') || 'Hide', htmlB: iconHtml('reveal') || 'Reveal',
        showA: !((entry.statuses as { target?: StatusEntry[] } | undefined)?.target ?? []).some(child =>
            str(pick(child, 'status_item_id')).toUpperCase() === INVENTORY_HIDDEN_ID),
        titleA: 'Hide Status Effect', titleB: 'Reveal Status Effect',
        // Lane GMS S2 / N6e (2026-10-02, R-065): client truth ONLY (status_truth_commit_inc.php) — hide_status_effect.php /
        // reveal_status_effect.php are DELETED; the seam locates the holder (a creature or an item) and refuses with words otherwise.
        onA: () => { if (window.ftTryCommitStatusHidden) void window.ftTryCommitStatusHidden({ statusId, hidden: true }); else if (typeof displayWarning === 'function') displayWarning('Reload the page to change this status.'); },
        onB: () => { if (window.ftTryCommitStatusHidden) void window.ftTryCommitStatusHidden({ statusId, hidden: false }); else if (typeof displayWarning === 'function') displayWarning('Reload the page to change this status.'); },
    }));
    btnRow.appendChild(hideWrap);

    // Pause/resume use the same resolved context as their existing callbacks.
    const pausable = flagOn(pick(def, 'status_effect_pausable'));
    const campaignId = statusCampaignId(entry, ctx.campaignId);
    const encounterId = statusEncounterId(ctx.encounterId);
    if (pausable && campaignId && encounterId && !isCraftingStatusEntry(entry, def)) {
        const pauseWrap = document.createElement('div'); pauseWrap.className = 'p-2';
        const paused = isPaused(entry);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = paused ? 'btn btn-outline-success btn-sm' : 'btn btn-outline-secondary border btn-sm';
        btn.title = paused ? 'Resume Status Effect' : 'Pause Status Effect';
        btn.setAttribute('aria-label', btn.title);
        btn.innerHTML = paused ? (iconHtml('resume') || 'Resume') : (iconHtml('pause') || 'Pause');
        btn.addEventListener('click', () => {
            if (paused) {
                if (typeof resumeStatusEffect === 'function') resumeStatusEffect(statusId, campaignId, encounterId);
            } else if (typeof pauseStatusEffect === 'function') {
                pauseStatusEffect(statusId, campaignId, encounterId);
            }
        });
        pauseWrap.appendChild(btn);
        btnRow.appendChild(pauseWrap);
    }

    const delWrap = document.createElement('div'); delWrap.className = 'ms-auto p-2';
    const delBtn = document.createElement('button');
    delBtn.type = 'button'; delBtn.className = 'btn btn-danger btn-sm';
    delBtn.innerHTML = iconHtml('delete') || 'Delete';
    delBtn.addEventListener('click', () => {
        // Mark pending so the parent list's echo repaint drops this row immediately instead of waiting
        // for the delete's own caster_update (matches the list-row delete buttons).
        markStatusEffectDeleted(statusId);
        // Lane GMS S6 / N6e (2026-10-02, R-065): client truth ONLY (target-row authority, the status row's creator) —
        // delete_status_effect_all_connections_inc.php is DELETED; the seam refuses with words what it cannot remove.
        if (window.ftTryCommitStatusDelete) void window.ftTryCommitStatusDelete({ statusId, scope: 'all_gm' });
        else if (typeof displayWarning === 'function') displayWarning('Reload the page to change this status.');
    });
    delWrap.appendChild(delBtn);
    btnRow.appendChild(delWrap);
    wrap.appendChild(btnRow);

    // Caster / target / origin display — skipped in compact (info panel above already shows them).
    const info = section();
    if (compact) info.className = 'mb-2'; // no extra bordered box when embedded
    if (!compact) {
        const casterId = str(pick(entry, 'status_caster_id'));
        const targetId = str(pick(entry, 'status_target_id'));
        const originId = str(pick(entry, 'status_origin_id'));
        if (casterId) {
            const b = section();
            const badge = document.createElement('span'); badge.className = 'badge bg-primary'; badge.appendChild(iconSpan('caster'));
            b.appendChild(badge); b.appendChild(document.createTextNode(' '));
            b.appendChild(clickableRef(casterId, str(pick(entry, 'status_caster_name')), str(pick(entry, 'status_caster_type'))));
            info.appendChild(b);
        }
        if (targetId) {
            const b = section();
            const badge = document.createElement('span'); badge.className = 'badge bg-danger'; badge.appendChild(iconSpan('target'));
            b.appendChild(badge); b.appendChild(document.createTextNode(' '));
            b.appendChild(clickableRef(targetId, str(pick(entry, 'status_target_name')), str(pick(entry, 'status_target_type'))));
            info.appendChild(b);
        }
        if (originId) {
            const b = section();
            const inner = document.createElement('div');
            inner.appendChild(iconSpan('status_effect_origin')); inner.appendChild(document.createTextNode(': '));
            inner.appendChild(clickableRef(originId, str(pick(entry, 'status_origin_id_item_name')) || str(pick(entry, 'status_origin_name')) || effectName, str(pick(entry, 'status_origin_id_item_type'))));
            b.appendChild(inner);
            info.appendChild(b);
        }
    }

    // Editable start/end time blocks (ids modifyCreatureStatusEffect() reads on save)
    info.appendChild(timeBlock(entry, 'starts', 'campaign_start_date', 'Status Effect Start:'));
    info.appendChild(timeBlock(entry, 'ends', 'campaign_end_date', 'Status Effect End:'));
    wrap.appendChild(info);

    // Save
    const saveRow = document.createElement('div'); saveRow.className = 'd-flex justify-content-center flex-wrap';
    const saveWrap = document.createElement('div'); saveWrap.className = 'p-1';
    const saveBtn = document.createElement('button'); saveBtn.className = 'btn btn-sm btn-primary'; saveBtn.textContent = 'Modify Status Effect';
    saveBtn.addEventListener('click', () => {
        // The existing callback reads these twelve ids synchronously. A card and sidelog editor
        // may coexist, so expose this editor's fields for that read, then restore the other ids.
        const displaced: Array<[HTMLElement, string]> = [];
        wrap.querySelectorAll<HTMLInputElement>('input[id^="status_"]').forEach(field => {
            document.querySelectorAll<HTMLElement>(`[id="${field.id}"]`).forEach(other => {
                if (other === field) return;
                displaced.push([other, other.id]);
                other.removeAttribute('id');
            });
        });
        try { if (typeof modifyCreatureStatusEffect === 'function') modifyCreatureStatusEffect(statusId); }
        finally { for (const [field, id] of displaced) field.id = id; }
    });
    saveWrap.appendChild(saveBtn); saveRow.appendChild(saveWrap);
    wrap.appendChild(saveRow);

    container.innerHTML = '';
    container.appendChild(wrap);
}
